import { useEffect, useId, useMemo, useState } from "react"
import {
  customFieldLabel,
  type Business,
  type Contact,
  type CustomField,
  type Household,
  type Pipeline,
  type PipelineAudienceType,
  type PipelineStage,
  type Tag,
} from "@/schema"
import { zodResolver } from "@hookform/resolvers/zod"
import { DateInput } from "@ui/forms/inputs/date-input"
import { TextInput } from "@ui/forms/inputs/text-input"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@ui/shad"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@ui/shad/select"
import { useForm } from "react-hook-form"
import { toast } from "sonner"
import { z } from "zod"

import { useFetchCustomFields } from "@/hooks/api/custom_fields"
import { useCreateOpportunity } from "@/hooks/api/opportunities"
import { useFetchTags } from "@/hooks/api/tags"
import { useFetchCurrentUser } from "@/hooks/api/users"
import { CustomFieldFormInput } from "@/components/custom-fields"
import { GridFormDialog } from "@/components/dialogs"
import TaskCreateRelationshipSelector from "@/components/tasks/create/inputs/task-create-relationship-selector"

import { UserSelectorField } from "./create/user-selector-field"
import { OpportunityLabelSelector } from "./opportunity-label-selector"

interface CreateOpportunityDialogProps {
  pipelineId: string
  stages: PipelineStage[]
  pipelines?: Pipeline[]
  defaultStageId?: string
  householdType?: "prospect" | "client" | null
  pipelineAudienceTypes?: PipelineAudienceType[]
  defaultHouseholdId?: string
  defaultHouseholdName?: string
  defaultOpportunityable?: {
    type: "Contact" | "Business"
    id: string
    name: string
  }
  bookId?: string
  onClose: () => void
}

const createOpportunitySchema = z.object({
  name: z.string().min(1, "Name is required"),
  pipeline_stage_id: z.string().min(1, "Stage is required"),
  value: z.coerce.number().min(0, "Value must be 0 or greater").nullable(),
  target_close_date: z.string().nullable(),
  owner_id: z.string().nullable(),
})

const getDefaultTargetCloseDate = () => {
  const date = new Date()
  date.setMonth(date.getMonth() + 1)
  return date.toISOString()
}

export const getFirstStageId = (stages: PipelineStage[]) => {
  const identifiedStage = stages.find((stage) => stage.name.toLowerCase() === "identified")
  return identifiedStage?.id || stages[0]?.id || ""
}

type CreateOpportunityFormData = z.infer<typeof createOpportunitySchema>

export function CreateOpportunityDialog({
  pipelineId: initialPipelineId,
  stages: initialStages,
  pipelines,
  defaultStageId,
  householdType,
  pipelineAudienceTypes,
  defaultHouseholdId,
  defaultHouseholdName,
  defaultOpportunityable,
  bookId,
  onClose,
}: CreateOpportunityDialogProps) {
  const dialogFormId = useId()
  const showPipelineSelector = pipelines && pipelines.length > 1
  const [selectedPipelineId, setSelectedPipelineId] = useState(initialPipelineId)
  const activePipelineId = showPipelineSelector ? selectedPipelineId : initialPipelineId
  const activePipeline = showPipelineSelector ? pipelines.find((p) => p.id === selectedPipelineId) : undefined
  const activeStages = useMemo(() => {
    if (!showPipelineSelector) return initialStages
    return pipelines.find((p) => p.id === selectedPipelineId)?.pipeline_stages ?? initialStages
  }, [showPipelineSelector, pipelines, selectedPipelineId, initialStages])
  const fallbackAudienceTypes = (householdType ? [householdType] : []) as PipelineAudienceType[]
  const activeAudienceTypes = activePipeline?.audience_types ?? pipelineAudienceTypes ?? fallbackAudienceTypes
  const normalizedAudienceTypes =
    activeAudienceTypes.length > 0
      ? activeAudienceTypes
      : (["client", "prospect", "contact", "business"] as PipelineAudienceType[])
  const householdAudienceTypes = normalizedAudienceTypes.filter((type) => type === "client" || type === "prospect")
  const householdTypeFilter =
    householdAudienceTypes.length === 1 ? (householdAudienceTypes[0] as "client" | "prospect") : undefined

  const createOpportunity = useCreateOpportunity(activePipelineId)
  const { data: currentUser } = useFetchCurrentUser()
  const { data: customFieldsData } = useFetchCustomFields("Opportunity")
  const { data: tagsData } = useFetchTags("opportunity", { bookIdOverride: bookId })
  const customFields = useMemo(() => customFieldsData?.custom_fields ?? [], [customFieldsData])
  const hasLabels = (tagsData?.tags?.length ?? 0) > 0
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, unknown>>({})
  const [label, setLabel] = useState<Tag | null>(null)
  const [selectedHousehold, setSelectedHousehold] = useState<Household | null>(null)
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null)
  const [selectedBusiness, setSelectedBusiness] = useState<Business | null>(null)

  const form = useForm<CreateOpportunityFormData>({
    resolver: zodResolver(createOpportunitySchema),
    defaultValues: {
      name: "",
      pipeline_stage_id: defaultStageId || getFirstStageId(activeStages),
      value: null,
      target_close_date: getDefaultTargetCloseDate(),
      owner_id: currentUser?.id || null,
    },
  })

  // Update owner_id when currentUser loads
  useEffect(() => {
    if (currentUser?.id && !form.getValues("owner_id")) {
      form.setValue("owner_id", currentUser.id)
    }
  }, [currentUser, form])

  const onSubmit = async (data: CreateOpportunityFormData) => {
    if (createOpportunity.isPending) return
    const opportunityableParams = defaultHouseholdId
      ? { household_id: defaultHouseholdId }
      : defaultOpportunityable
        ? { opportunityable_type: defaultOpportunityable.type, opportunityable_id: defaultOpportunityable.id }
        : selectedHousehold
          ? { opportunityable_type: "Household" as const, opportunityable_id: selectedHousehold.id }
          : selectedContact
            ? { opportunityable_type: "Contact" as const, opportunityable_id: selectedContact.id }
            : selectedBusiness
              ? { opportunityable_type: "Business" as const, opportunityable_id: selectedBusiness.id }
              : {}

    await createOpportunity.mutateAsync({
      opportunity: {
        ...data,
        ...opportunityableParams,
        value: data.value || undefined,
        target_close_date: data.target_close_date ? new Date(data.target_close_date) : undefined,
        ...(Object.keys(customFieldValues).length > 0 ? { custom_fields_attributes: customFieldValues } : {}),
        ...(label ? { tag_ids: [label.id] } : {}),
      },
      overrideBookId: bookId,
    })
    toast.success("Opportunity created successfully")
    onClose()
  }

  return (
    <GridFormDialog
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
      title={"New opportunity"}
      action={{
        label: "Create opportunity",
        form: dialogFormId,
        disabled: createOpportunity.isPending,
        pending: createOpportunity.isPending,
      }}
      cancel={{ label: "Cancel", onClick: onClose, disabled: createOpportunity.isPending }}
    >
      <Form {...form}>
        <form id={dialogFormId} onSubmit={form.handleSubmit(onSubmit)} className="flex min-h-0 flex-1 flex-col gap-2">
          <div className="grid grid-cols-2 gap-2 space-y-0">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <TextInput {...field} id="name" placeholder="Enter opportunity name" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {showPipelineSelector && (
              <FormItem>
                <FormLabel>Pipeline</FormLabel>
                <Select
                  value={selectedPipelineId}
                  onValueChange={(value) => {
                    setSelectedPipelineId(value)
                    const newStages = pipelines.find((p) => p.id === value)?.pipeline_stages ?? []
                    form.setValue("pipeline_stage_id", getFirstStageId(newStages))
                    setSelectedHousehold(null)
                    setSelectedContact(null)
                    setSelectedBusiness(null)
                  }}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a pipeline" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {pipelines.map((pipeline) => (
                      <SelectItem key={pipeline.id} value={pipeline.id}>
                        {pipeline.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormItem>
            )}

            <FormField
              control={form.control}
              name="pipeline_stage_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Stage</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a stage" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {activeStages.map((stage) => (
                        <SelectItem key={stage.id} value={stage.id}>
                          {stage.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="value"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Value</FormLabel>
                  <FormControl>
                    <TextInput
                      {...field}
                      id="value"
                      type="number"
                      placeholder="0"
                      value={field.value ?? ""}
                      onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : null)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="target_close_date"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Target close date</FormLabel>
                  <FormControl>
                    <DateInput
                      id="target_close_date"
                      value={field.value ? new Date(field.value) : undefined}
                      onChange={(date) => field.onChange(date?.toISOString() || null)}
                      placeholder="Select date"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {defaultHouseholdId && defaultHouseholdName ? (
              <FormItem>
                <FormLabel>
                  {householdType ? householdType.charAt(0).toUpperCase() + householdType.slice(1) : "Household"}
                </FormLabel>
                <FormControl>
                  <TextInput id="household_id" value={defaultHouseholdName} disabled />
                </FormControl>
              </FormItem>
            ) : defaultOpportunityable ? (
              <FormItem>
                <FormLabel>{defaultOpportunityable.type}</FormLabel>
                <FormControl>
                  <TextInput id="opportunityable_id" value={defaultOpportunityable.name} disabled />
                </FormControl>
              </FormItem>
            ) : (
              <FormItem>
                <FormLabel>Related record</FormLabel>
                <FormControl>
                  <TaskCreateRelationshipSelector
                    selectedHousehold={selectedHousehold}
                    onHouseholdSelect={setSelectedHousehold}
                    selectedContact={selectedContact}
                    onContactSelect={setSelectedContact}
                    selectedBusiness={selectedBusiness}
                    onBusinessSelect={setSelectedBusiness}
                    allowHouseholds={householdAudienceTypes.length > 0}
                    allowContacts={normalizedAudienceTypes.includes("contact")}
                    allowBusinesses={normalizedAudienceTypes.includes("business")}
                    householdType={householdTypeFilter}
                    bookId={bookId}
                    variant="field"
                    className="h-9"
                  />
                </FormControl>
              </FormItem>
            )}

            <FormField
              control={form.control}
              name="owner_id"
              render={({ field }) => <UserSelectorField field={field} bookIdOverride={bookId} />}
            />

            {hasLabels && (
              <FormItem>
                <FormLabel>Label</FormLabel>
                <FormControl>
                  <OpportunityLabelSelector label={label} onLabelChange={setLabel} />
                </FormControl>
              </FormItem>
            )}

            {customFields.map((field: CustomField) => (
              <FormItem key={field.id}>
                <FormLabel>{customFieldLabel(field)}</FormLabel>
                <FormControl>
                  <CustomFieldFormInput
                    customField={field}
                    value={customFieldValues[field.field_key]}
                    onSave={(val) => setCustomFieldValues((prev) => ({ ...prev, [field.field_key]: val }))}
                    isLoading={false}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            ))}
          </div>
        </form>
      </Form>
    </GridFormDialog>
  )
}
